import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Send, Plus, Trash2, ShieldCheck, AlertTriangle, Loader2, Copy, BellRing, FileSpreadsheet, Lightbulb, TrendingUp, Receipt, ClipboardCheck, PenLine, Sparkles, Save, CopyPlus, BookmarkPlus, Eye, Check, Camera } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import { StudioLayout } from "./StudioLayout";
import { ClientQuestions } from "@/components/studio/ClientQuestions";
import { DuplicateQuoteDialog } from "@/components/studio/DuplicateQuoteDialog";
import { SaveTemplateDialog } from "@/components/studio/SaveTemplateDialog";
import { studio, apiError, beDate, eur, inputCls, btnPrimary, btnGhost, VAT_RATES, sym } from "@/lib/studioApi";
import { useFiscal } from "@/lib/fiscal";
import { FiscalPanel } from "@/components/studio/FiscalPanel";
import { MaterialImport } from "@/components/MaterialImport";
import { QuoteAssistant } from "@/components/studio/QuoteAssistant";
import { MoreActionsMenu } from "@/components/studio/MoreActionsMenu";
import { PageSkeleton } from "@/components/studio/StudioUI";
import { MeasureCamera } from "@/components/studio/MeasureCamera";
import { SignaturePad } from "@/components/studio/SignaturePad";
import { QuoteSummary } from "@/components/studio/QuoteSummary";
import { PdfPreviewDialog } from "@/components/studio/PdfPreviewDialog";

const LINE_KINDS = { fourniture: "Fournitures", main_oeuvre: "Main-d'œuvre", autre: "Autres postes" };
const LABOUR_UNITS = ["heure", "heures", "h", "hr", "jour", "jours", "journée"];
const LABOUR_WORDS = ["pose", "raccordement", "installation", "montage", "démontage", "dépose", "purge", "mise en service", "remise en service", "déplacement", "main-d", "main d", "nettoyage", "évacuation", "vérification", "réglage", "contrôle", "mise en route", "préparation", "démolition", "placement", "tirage", "câblage", "soudure", "peinture", "ponçage", "carrelage", "intervention", "diagnostic", "entretien", "réparation", "remplacement", "mise en conformité", "test", "essai"];
const SUPPLY_UNITS = ["pièce", "pièces", "pce", "pc", "m", "ml", "m²", "m2", "m³", "m3", "kg", "l", "litre", "sac", "rouleau", "boîte", "paquet", "lot", "unité"];
const SUPPLY_WORDS = ["pièce", "kit", "filtre", "chaudière", "radiateur", "vanne", "tuyau", "câble", "disjoncteur", "robinet", "pompe", "ballon", "cuve", "inhibiteur", "produit", "panneau", "plaque", "profilé", "vis", "joint", "raccord", "colle", "mortier", "ciment", "sable", "carreau", "parquet", "porte", "fenêtre", "châssis", "lampe", "luminaire"];
export const detectKind = (l) => {
  if (l.kind_manual && LINE_KINDS[l.kind]) return l.kind;
  const unit = (l.unit || "").trim().toLowerCase();
  const text = (l.description || "").trim().toLowerCase();
  if (LABOUR_UNITS.includes(unit)) return "main_oeuvre";
  if (["fourniture", "livraison", "matériel", "matériaux"].some((w) => text.includes(w))) return "fourniture";
  if (LABOUR_WORDS.some((w) => text.startsWith(w)) || (LABOUR_WORDS.some((w) => text.includes(w)) && ["forfait", "forf", ""].includes(unit))) return "main_oeuvre";
  if (SUPPLY_UNITS.includes(unit) || SUPPLY_WORDS.some((w) => text.includes(w))) return "fourniture";
  if (LABOUR_WORDS.some((w) => text.includes(w))) return "main_oeuvre";
  return "autre";
};

const emptyLine = { description: "", quantity: 1, unit: "forfait", unit_price: 0, vat_percent: 21, cost_price: 0, duration_minutes: 0 };

const STATUS_LABEL = { draft: "Brouillon", sent: "Envoyé", viewed: "Consulté", accepted: "Accepté", refused: "Refusé" };
const STATUS_CLS = {
  draft: "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]",
  sent: "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]",
  viewed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  accepted: "bg-green-50 text-green-700 border-green-200",
  refused: "bg-red-50 text-red-600 border-red-200",
};

export default function StudioQuote() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [lines, setLines] = useState([]);
  const [conditions, setConditions] = useState("");
  const [fiscalForm, setFiscalForm] = useState({ client_country: "", client_type: "", client_vat: "", fiscal_confirmed: false });
  const fiscalRules = useFiscal();
  const [remarks, setRemarks] = useState("");
  const [services, setServices] = useState([]);
  const [profile, setProfile] = useState({});
  const [depositPercent, setDepositPercent] = useState(30);
  const [warnings, setWarnings] = useState([]);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [isOwner, setIsOwner] = useState(true);
  const [showSign, setShowSign] = useState(false);
  const [mode, setMode] = useState("view");
  const [preview, setPreview] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [signName, setSignName] = useState("");
  const [signEmail, setSignEmail] = useState("");
  const [signOption, setSignOption] = useState("");
  const pad = useRef(null);

  const load = useCallback(() => {
    studio.get(`/quotes/${id}`).then(({ data }) => {
      setQuote(data);
      setLines(data.lines.map((l) => ({ ...l })));
      setConditions(data.conditions || "");
      setFiscalForm({ client_country: data.client_country || "", client_type: data.client_type || "", client_vat: data.client_vat || "", fiscal_confirmed: !!(data.fiscal && data.fiscal.confirmed), fiscal: data.fiscal });
      setRemarks(data.remarks || "");
      if (data.status === "draft" && !data.lines.some((l) => (l.description || "").trim())) setMode("edit");
    }).catch(() => toast.error("Devis introuvable."));
    studio.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    studio.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    studio.get("/whoami").then(({ data }) => setIsOwner(!!data.is_owner)).catch(() => {});
    const stored = sessionStorage.getItem(`studio_warnings_${id}`);
    if (stored) setWarnings(JSON.parse(stored));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const sumLines = (arr) => arr.reduce((acc, l) => {
    const net = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    acc.net += net;
    acc.vat += (net * (Number(l.vat_percent) || 0)) / 100;
    return acc;
  }, { net: 0, vat: 0 });
  const optionNames = [...new Set(lines.map((l) => (l.option || "").trim()).filter(Boolean))];
  const chosenOption = quote?.chosen_option || "";
  const baseLines = lines.filter((l) => !(l.option || "").trim());
  const totals = sumLines(chosenOption ? lines.filter((l) => !(l.option || "").trim() || (l.option || "").trim() === chosenOption) : baseLines);
  const optionTotals = optionNames.map((name) => ({ name, ...sumLines([...baseLines, ...lines.filter((l) => (l.option || "").trim() === name)]) }));
  const effectiveLines = chosenOption ? lines.filter((l) => !(l.option || "").trim() || (l.option || "").trim() === chosenOption) : baseLines;
  const kindTotals = Object.keys(LINE_KINDS).map((k) => ({ kind: k, net: sumLines(effectiveLines.filter((l) => detectKind(l) === k)).net })).filter((k) => k.net > 0);

  const setLineOption = (i, value) => {
    if (value === "__new__") {
      const name = window.prompt("Nom de l'option (ex. : Confort, Premium, Pompe à chaleur)");
      if (!name?.trim()) return;
      value = name.trim();
    }
    setLines((prev) => prev.map((x, idx) => (idx === i ? { ...x, option: value } : x)));
  };

  const hourlyCost = Number(profile.hourly_cost) || 0;
  const vatRates = fiscalRules?.rates || VAT_RATES[profile.vat_country || "BE"] || VAT_RATES.BE;
  const hasReverseCharge = lines.some((l) => l.reverse_charge);
  const targetMargin = Number(profile.target_margin_percent) || 0;
  const prof = lines.reduce((acc, l) => {
    const qty = Number(l.quantity) || 0;
    const cost = Number(l.cost_price) || 0;
    const minutes = Number(l.duration_minutes) || 0;
    acc.materialCost += qty * cost;
    acc.minutes += qty * minutes;
    if (cost <= 0) acc.missingCost += 1;
    if (minutes <= 0) acc.missingDuration += 1;
    return acc;
  }, { materialCost: 0, minutes: 0, missingCost: 0, missingDuration: 0 });
  const hours = prof.minutes / 60;
  const labourCost = hours * hourlyCost;
  const margin = totals.net - prof.materialCost - labourCost;
  const marginPercent = totals.net ? (margin / totals.net) * 100 : 0;
  const marginPerHour = hours ? margin / hours : 0;
  const marginState = margin < 0 ? "loss" : marginPercent < targetMargin ? "below" : "ok";
  const MARGIN_PILL = {
    loss: ["À perte", "bg-rose-500/15 text-rose-200 border-rose-400/40"],
    below: ["Sous l'objectif", "bg-amber-400/15 text-amber-200 border-amber-300/40"],
    ok: ["Objectif atteint", "bg-emerald-400/15 text-emerald-200 border-emerald-300/40"],
  };

  const polish = async () => {
    if (!lines.some((l) => (l.description || "").trim())) return toast.error("Ajoutez au moins une ligne avec une description.");
    setBusy("polish");
    const before = lines.map((l) => ({ ...l }));
    try {
      const { data } = await studio.post(`/quotes/${id}/polish`, { lines }, { timeout: 300000 });
      setLines(data.lines.map((l) => ({ ...l })));
      toast.success("Descriptions réécrites pour le client — pensez à enregistrer.", {
        action: { label: "Annuler", onClick: () => setLines(before) },
      });
    } catch (e) { toast.error(apiError(e, "Rédaction impossible.")); } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await studio.put(`/quotes/${id}`, {
        dossier_id: quote.dossier_id,
        lines: lines.map(({ description, quantity, unit, unit_price, vat_percent, cost_price, duration_minutes, reverse_charge, option, kind, kind_manual }) => ({
          description, quantity: Number(quantity) || 0, unit, unit_price: Number(unit_price) || 0, option: (option || "").trim(), kind: kind || "", kind_manual: !!kind_manual,
          vat_percent: reverse_charge ? 0 : Number(vat_percent) || 0, reverse_charge: !!reverse_charge,
          cost_price: Number(cost_price) || 0, duration_minutes: Number(duration_minutes) || 0,
        })),
        conditions, remarks,
        client_country: fiscalForm.client_country, client_type: fiscalForm.client_type, client_vat: fiscalForm.client_vat, fiscal_confirmed: !!fiscalForm.fiscal_confirmed,
      });
      setQuote(data);
      toast.success(quote.status === "draft" ? "Brouillon enregistré. Reprenez-le quand vous voulez depuis le dossier ou la liste des devis." : "Devis enregistré.");
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible."));
    } finally { setBusy(""); }
  };

  const removeQuote = async () => {
    if (!window.confirm(quote.status === "draft" ? `Supprimer le brouillon ${quote.number} ? Cette action est définitive.` : `Supprimer le devis ${quote.number} ?`)) return;
    setBusy("delete");
    try {
      await studio.delete(`/quotes/${id}`);
      toast.success(`${quote.number} supprimé.`);
      navigate(quote.dossier_id ? `/studio/dossiers/${quote.dossier_id}` : "/studio/devis");
    } catch (e) { toast.error(apiError(e, "Suppression impossible.")); setBusy(""); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await studio.get(`/quotes/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `devis-${quote.number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Téléchargement impossible."); } finally { setBusy(""); }
  };

  const runReview = async () => {
    setBusy("review");
    try {
      const { data } = await studio.post(`/quotes/${id}/review`, {}, { timeout: 300000 });
      setReview(data);
      toast.success(data.ok ? "Le devis semble complet." : "L'assistant a repéré des points à vérifier.");
    } catch (e) { toast.error(apiError(e, "Vérification impossible.")); } finally { setBusy(""); }
  };

  const sendToClient = async () => {
    setBusy("send");
    try {
      const { data } = await studio.post(`/quotes/${id}/send`);
      toast.success("Devis envoyé au client.");
      copyToClipboard(data.link);
      load();
    } catch (e) { toast.error(apiError(e, "Envoi impossible.")); } finally { setBusy(""); }
  };

  const remind = async () => {
    try {
      await studio.post(`/quotes/${id}/remind`);
      toast.success("Relance envoyée.");
      load();
    } catch (e) { toast.error(apiError(e, "Relance impossible.")); }
  };

  const signOnsite = async () => {
    if (!signName.trim()) return toast.error("Indiquez le nom du client qui signe.");
    const signature_data = pad.current?.toDataURL() || "";
    if (!signature_data) return toast.error("Faites signer le client dans le cadre prévu.");
    setBusy("onsite");
    try {
      if (optionNames.length && !signOption) return toast.error("Choisissez l'option retenue par le client.");
      await studio.post(`/quotes/${id}/sign-onsite`, { name: signName, email: signEmail, signature_data, option: signOption });
      toast.success("Devis signé sur place. Vous pouvez facturer.");
      setShowSign(false);
      setSignName("");
      setSignEmail("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Signature impossible."));
    } finally {
      setBusy("");
    }
  };

  const addMissingLine = (item) => {
    setLines([...lines, {
      description: item.description, quantity: item.quantity, unit: item.unit,
      unit_price: item.unit_price, vat_percent: item.vat_percent,
      cost_price: item.cost_price || 0, duration_minutes: item.duration_minutes || 0,
    }]);
    setReview({ ...review, missing_lines: review.missing_lines.filter((m) => m.id !== item.id) });
    toast.success("Ligne ajoutée — pensez à enregistrer le devis");
  };

  const useClientNote = () => {
    setRemarks(remarks ? `${remarks}\n\n${review.client_note}` : review.client_note);
    toast.success("Note ajoutée aux remarques du devis");
  };

  const PROVIDERS = { billit: "Billit", yuki: "Yuki", odoo: "Odoo" };

  const pushTo = async (provider) => {
    setBusy(provider);
    try {
      const { data } = await studio.post(`/quotes/${id}/push/${provider}`);
      const label = PROVIDERS[provider];
      toast.success(data.replayed
        ? `Déjà transféré dans ${label} (réf. ${data.remote_id}).`
        : `Facture créée dans ${label} (réf. ${data.remote_id})${data.state ? ` — ${data.state}` : ""}.`);
      load();
    } catch (e) { toast.error(apiError(e, "Transfert impossible.")); } finally { setBusy(""); }
  };

  const exportQuote = async (mode) => {
    try {
      const { data } = await studio.get(`/quotes/${id}/export`);
      if (mode === "copy") {
        await copyToClipboard(data.text, "Devis copié — collez-le dans votre logiciel.");
      } else {
        const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `devis-${quote.number}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      }
    } catch (e) { toast.error("Export impossible."); }
  };

  if (!quote) return <StudioLayout title="Devis"><PageSkeleton testId="quote-skeleton" /></StudioLayout>;

  const publicLink = `${window.location.origin}/devis/${quote.public_token}`;
  const editable = !["accepted", "refused"].includes(quote.status);

  return (
    <StudioLayout title={`Devis ${quote.number}`} subtitle={`${quote.client_name} · ${beDate(quote.issue_date)} · valable jusqu'au ${beDate(quote.valid_until)}`}>
      <button type="button" data-testid="quote-back" onClick={() => navigate(`/studio/dossiers/${quote.dossier_id}`)} className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#5C6A87] hover:text-[#0F2645] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Retour au dossier
      </button>

      <ClientQuestions docId={quote.id} docType="devis" />

      {/* ---------- Barre récapitulative collante ---------- */}
      <div
        data-testid="quote-sticky-bar"
        className="sticky top-[61px] z-30 -mx-4 mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-[#E2E7F7] bg-white/95 px-4 py-2.5 shadow-[0_8px_24px_-16px_rgba(8,16,77,0.25)] backdrop-blur-md sm:-mx-6 sm:px-6 lg:-mx-10 lg:top-0 lg:rounded-b-2xl lg:border-t-0 lg:px-10"
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="font-mono text-xs font-bold text-[#5C6A87]">{quote.number}</span>
          <span data-testid="quote-status" className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${STATUS_CLS[quote.status]}`}>{STATUS_LABEL[quote.status]}</span>
          {quote.signature && <span className="hidden items-center gap-1 text-[11px] font-bold text-emerald-700 sm:inline-flex"><ShieldCheck className="h-3.5 w-3.5" /> Signé</span>}
        </div>
        <div className="ml-auto flex items-baseline gap-2">
          <span className="hidden text-[11px] font-semibold uppercase tracking-wide text-[#93A0BC] sm:inline">Total TVAC</span>
          <span data-testid="quote-sticky-total" className="font-mono text-lg font-extrabold tabular-nums text-[#0F2645]">{eur(totals.net + totals.vat)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="quote-preview-top" onClick={() => setPreview(true)} aria-label="Visualiser le devis" title="Visualiser le devis" className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-[#E2E7F7] bg-white text-[#3A5CC8] transition-colors hover:border-[#C9D4F4] hover:bg-[#F6F8FD]">
            <Eye className="h-4 w-4" />
          </button>
          <button type="button" data-testid="quote-save-top" disabled={busy === "save"} onClick={save} className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border border-[#E2E7F7] bg-white px-3.5 text-xs font-bold text-[#0F2645] transition-colors hover:border-[#3A5CC8] disabled:opacity-40">
            {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer
          </button>
          {editable && (
            <button type="button" data-testid="quote-send-top" disabled={busy === "send"} onClick={sendToClient} className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl bg-[#3A5CC8] px-3.5 text-xs font-bold text-white shadow-[0_6px_16px_-8px_rgba(58,92,200,0.9)] transition-all hover:-translate-y-0.5 hover:bg-[#3A5CC8] disabled:opacity-40">
              {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Envoyer
            </button>
          )}
          {["draft", "refused", "expired", "cancelled"].includes(quote.status) && (
            <button type="button" data-testid="quote-delete-top" disabled={busy === "delete"} onClick={removeQuote} aria-label="Supprimer le devis" title={quote.status === "draft" ? "Supprimer ce brouillon" : "Supprimer ce devis"} className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-[#E2E7F7] bg-white text-[#93A0BC] transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40">
              {busy === "delete" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {quote.signature && <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-bold text-green-700"><ShieldCheck className="w-3.5 h-3.5" /> Signé par {quote.signature.name}</span>}
        {quote.reminders_sent > 0 && <span className="text-xs text-[#5C6A87]">{quote.reminders_sent} relance(s)</span>}
      </div>

      {!!warnings.length && (
        <div data-testid="quote-warnings" className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm font-bold text-amber-900 inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Devis intelligent — à vérifier</p>
          <ul className="mt-2 space-y-1.5">
            {warnings.map((w, i) => <li key={i} className="text-sm text-amber-900">• {w}</li>)}
          </ul>
        </div>
      )}

      {quote.status === "accepted" && (
        <div
          data-testid="quote-next-step"
          className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 sm:px-5"
        >
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 font-heading text-base font-bold text-emerald-900">
              <ShieldCheck className="h-4 w-4" /> Devis signé — prochaine étape
            </p>
            <p className="mt-1 text-sm leading-relaxed text-emerald-800">
              {quote.intervention_id
                ? "L'intervention est déjà créée : ouvrez-la pour cocher les travaux et faire signer le rapport."
                : "Créez l'intervention : client, adresse, travaux et prix sont repris automatiquement du devis."}
            </p>
          </div>
          <button
            type="button"
            data-testid="quote-to-intervention"
            disabled={busy === "intervention"}
            onClick={async () => {
              if (quote.intervention_id) return navigate(`/studio/interventions/${quote.intervention_id}`);
              setBusy("intervention");
              try {
                const { data } = await studio.post("/interventions", {
                  dossier_id: quote.dossier_id,
                  quote_id: id,
                });
                toast.success(`Intervention ${data.reference} créée.`);
                navigate(`/studio/interventions/${data.id}`);
              } catch (e) {
                toast.error(apiError(e, "Création de l'intervention impossible."));
              } finally {
                setBusy("");
              }
            }}
            className="inline-flex min-h-[46px] shrink-0 items-center gap-2 rounded-xl bg-emerald-700 px-5 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-40"
          >
            {busy === "intervention" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
            {quote.intervention_id ? "Ouvrir l'intervention" : "Créer l'intervention"}
          </button>
        </div>
      )}

      {!["accepted", "refused"].includes(quote.status) && (
        <QuoteAssistant quoteId={id} lines={lines} setLines={setLines} />
      )}

      {mode === "view" && (
        <QuoteSummary
          quote={quote} lines={lines} totals={totals} optionNames={optionNames} chosenOption={chosenOption} optionTotals={optionTotals}
          kindTotals={kindTotals} detectKind={detectKind} remarks={remarks} conditions={conditions} editable={editable}
          onEdit={() => setMode("edit")} onPreview={() => setPreview(true)}
        />
      )}

      {mode === "edit" && (<>
      <div data-testid="quote-editor" className="mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]" style={{ animation: "devFadeUp .25s ease-out" }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Mode édition</p>
            <h2 className="font-heading text-base font-semibold text-[#0F2645]">Lignes du devis</h2>
          </div>
          <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="quote-edit-done" onClick={() => setMode("view")} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl bg-[#0F2645] px-3.5 text-xs font-bold text-white transition-colors hover:bg-[#1B3561]">
            <Check className="h-3.5 w-3.5" /> Terminer
          </button>
          {editable && (
            <button
              type="button"
              data-testid="quote-polish-btn"
              disabled={busy === "polish" || !lines.some((l) => (l.description || "").trim())}
              onClick={polish}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-[#3A5CC8]/40 bg-[#EEF1FC] px-3.5 text-xs font-bold text-[#2F4CA8] transition-colors hover:bg-[#3A5CC8] hover:text-white disabled:opacity-40"
            >
              {busy === "polish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {busy === "polish" ? "Rédaction…" : "Rédiger les descriptions avec l'IA"}
            </button>
          )}
          </div>
        </div>
        <div className="mt-4 space-y-3">
          {lines.map((l, i) => (
            <div key={i} data-testid={`quote-line-${i}`} className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-3 space-y-2">
              <div className="flex gap-2">
                <textarea data-testid={`line-desc-${i}`} rows={2} className={inputCls} value={l.description} onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  {editable ? (
                    <select
                      data-testid={`line-kind-${i}`}
                      value={detectKind(l)}
                      onChange={(e) => setLines((prev) => prev.map((x, idx) => (idx === i ? { ...x, kind: e.target.value, kind_manual: true } : x)))}
                      title="Type de poste (détecté automatiquement, modifiable)"
                      className={`max-w-[120px] rounded-lg border px-2 py-1 text-[11px] font-semibold outline-none ${detectKind(l) === "fourniture" ? "border-amber-200 bg-amber-50 text-amber-800" : detectKind(l) === "main_oeuvre" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-[#E2E7F7] bg-white text-[#5C6A87]"}`}
                    >
                      {Object.entries(LINE_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  ) : (
                    <span data-testid={`line-kind-${i}`} className="rounded-lg bg-white px-2 py-1 text-[11px] font-semibold text-[#5C6A87]">{LINE_KINDS[detectKind(l)]}</span>
                  )}
                  <button type="button" data-testid={`line-remove-${i}`} onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="text-[#93A0BC] hover:text-red-600 transition-colors" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
                  {editable && (
                    <select
                      data-testid={`line-option-${i}`}
                      value={(l.option || "").trim()}
                      onChange={(e) => setLineOption(i, e.target.value)}
                      title="Option (variante proposée au client)"
                      className={`max-w-[120px] rounded-lg border px-2 py-1 text-[11px] font-semibold outline-none ${(l.option || "").trim() ? "border-[#3A5CC8]/40 bg-[#EEF1FC] text-[#2F4CA8]" : "border-[#E2E7F7] bg-white text-[#93A0BC]"}`}
                    >
                      <option value="">Base</option>
                      {optionNames.map((n) => <option key={n} value={n}>{`Option ${n}`}</option>)}
                      <option value="__new__">+ Nouvelle option…</option>
                    </select>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <span className="hidden sm:block text-[10px] font-bold uppercase tracking-wide text-[#93A0BC] -mb-1">Quantité</span>
                <span className="hidden sm:block text-[10px] font-bold uppercase tracking-wide text-[#93A0BC] -mb-1">Unité</span>
                <span className="hidden sm:block text-[10px] font-bold uppercase tracking-wide text-[#93A0BC] -mb-1">P.U. HTVA</span>
                <span className="hidden sm:block text-[10px] font-bold uppercase tracking-wide text-[#93A0BC] -mb-1">TVA</span>
                <span className="hidden sm:block text-right text-[10px] font-bold uppercase tracking-wide text-[#93A0BC] -mb-1">Total HTVA</span>
                <input data-testid={`line-qty-${i}`} type="number" step="0.5" min="0" className={inputCls} value={l.quantity} onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))} />
                <input data-testid={`line-unit-${i}`} className={inputCls} placeholder="Unité" value={l.unit} onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, unit: e.target.value } : x))} />
                <input data-testid={`line-price-${i}`} type="number" step="0.01" min="0" className={inputCls} value={l.unit_price} onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, unit_price: e.target.value } : x))} />
                <select
                  data-testid={`line-vat-${i}`}
                  className={inputCls}
                  value={l.reverse_charge ? "ac" : String(l.vat_percent)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLines((prev) => prev.map((x, idx) => idx === i
                      ? (v === "ac" ? { ...x, reverse_charge: true, vat_percent: 0 } : { ...x, reverse_charge: false, vat_percent: v })
                      : x));
                  }}
                >
                  {vatRates.map((v) => <option key={v} value={v}>{`${v} % TVA`}</option>)}
                  <option value="ac">Autoliquidation</option>
                </select>
                <p className="self-center text-right text-sm font-bold text-[#0F2645]">{eur((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#93A0BC]">Coût d'achat {sym()}</span>
                  <input data-testid={`line-cost-${i}`} type="number" step="0.01" min="0" className={`${inputCls} mt-1`} placeholder="Coût d'achat €" value={l.cost_price ?? 0} onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, cost_price: e.target.value } : x))} />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#93A0BC]">Durée (min)</span>
                  <input data-testid={`line-duration-${i}`} type="number" step="5" min="0" className={`${inputCls} mt-1`} placeholder="Durée (min)" value={l.duration_minutes ?? 0} onChange={(e) => setLines((prev) => prev.map((x, idx) => idx === i ? { ...x, duration_minutes: e.target.value } : x))} />
                </label>
                {isOwner && (
                  <p data-testid={`line-margin-${i}`} className="col-span-2 self-end pb-2 text-right text-xs text-[#5C6A87]">
                    Marge ligne {eur(((Number(l.quantity) || 0) * ((Number(l.unit_price) || 0) - (Number(l.cost_price) || 0))) - (((Number(l.quantity) || 0) * (Number(l.duration_minutes) || 0)) / 60) * hourlyCost)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" data-testid="line-add-btn" onClick={() => setLines([...lines, { ...emptyLine, vat_percent: fiscalRules?.standard_rate ?? 21 }])} className={btnGhost}>
            <Plus className="w-3.5 h-3.5" /> Ligne libre
          </button>
          <button type="button" data-testid="line-measure-btn" onClick={() => setMeasuring(true)} className={btnGhost}>
            <Camera className="w-4 h-4 text-[#3A5CC8]" /> Mesurer avec la caméra
          </button>
          <select
            data-testid="line-add-service"
            className={`${inputCls} max-w-[18rem] py-2.5`}
            value=""
            onChange={(e) => {
              const s = services.find((x) => x.id === e.target.value);
              if (s) setLines([...lines, { description: s.description || s.name, quantity: 1, unit: s.unit, unit_price: s.price_htva, vat_percent: s.vat_percent, cost_price: s.cost_price || 0, duration_minutes: s.duration_minutes || 0 }]);
            }}
          >
            <option value="">Ajouter depuis mes tarifs…</option>
            {services.map((s) => <option key={s.id} value={s.id}>{`${s.name} — ${Number(s.price_htva).toFixed(2)} ${sym()}`}</option>)}
          </select>
        </div>

        <div className="mt-4"><FiscalPanel value={fiscalForm} onChange={setFiscalForm} /></div>
        <textarea data-testid="quote-remarks" rows={2} className={`${inputCls} mt-4`} placeholder="Remarques pour le client" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        <textarea data-testid="quote-conditions" rows={3} className={`${inputCls} mt-2`} placeholder="Conditions" value={conditions} onChange={(e) => setConditions(e.target.value)} />
      </div>

      <MaterialImport
        dossierId={quote.dossier_id}
        vatPercent={fiscalRules?.standard_rate ?? (vatRates.includes(21) ? 21 : vatRates[0])}
        onLines={(newLines, data) => {
          setLines((prev) => [...prev.filter((l) => (l.description || "").trim()), ...newLines]);
          if (data?.remarks && !remarks) setRemarks(data.remarks);
        }}
      />

      <div className="mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
        <div className="flex flex-wrap items-center gap-4">
          <p className="text-sm text-[#5C6A87]">HTVA <span className="font-semibold text-[#0F2645]">{eur(totals.net)}</span></p>
          <p className="text-sm text-[#5C6A87]">TVA <span className="font-semibold text-[#0F2645]">{eur(totals.vat)}</span></p>
          <p data-testid="quote-total" className="font-heading text-lg font-bold text-[#0F2645]">{optionNames.length && !chosenOption ? "Total base TVAC" : "Total TVAC"} {eur(totals.net + totals.vat)}</p>
          <button type="button" data-testid="quote-save-btn" disabled={busy === "save"} onClick={save} className={`${btnPrimary} ml-auto`}>
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
          </button>
          {kindTotals.length > 1 && (
            <div data-testid="quote-kind-totals" className="basis-full flex flex-wrap gap-2 pt-1">
              {kindTotals.map((k) => (
                <span key={k.kind} data-testid={`quote-kind-total-${k.kind}`} className={`rounded-full border px-3 py-1 text-xs font-semibold ${k.kind === "fourniture" ? "border-amber-200 bg-amber-50 text-amber-800" : k.kind === "main_oeuvre" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-[#E2E7F7] bg-white text-[#5C6A87]"}`}>
                  {LINE_KINDS[k.kind]} HTVA : <span className="font-mono font-bold">{eur(k.net)}</span>
                </span>
              ))}
            </div>
          )}
          {optionNames.length > 0 && (
            <div data-testid="quote-option-totals" className="basis-full flex flex-wrap gap-2 pt-1">
              {chosenOption && <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">Option retenue par le client : {chosenOption}</span>}
              {!chosenOption && optionTotals.map((o) => (
                <span key={o.name} data-testid={`quote-option-total-${o.name}`} className="rounded-full border border-[#3A5CC8]/30 bg-[#EEF1FC] px-3 py-1 text-xs font-semibold text-[#2F4CA8]">
                  Avec option {o.name} : <span className="font-mono font-bold">{eur(o.net + o.vat)}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      </>)}

      {quote.status === "accepted" && (
        <div data-testid="quote-invoice-actions" className="mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <p className="font-heading text-base font-semibold text-[#0F2645]">Facturer ce devis</p>
          <p className="mt-1 text-sm text-[#5C6A87] leading-relaxed">L'acompte est déduit automatiquement de la facture finale.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2">
              <input
                data-testid="quote-deposit-percent"
                type="number"
                min="1"
                max="100"
                value={depositPercent}
                onChange={(e) => setDepositPercent(e.target.value)}
                className="w-20 rounded-xl border border-[#E2E7F7] bg-white px-3 py-2 text-sm font-semibold text-[#0F2645] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
              />
              <span className="text-sm font-semibold text-[#5C6A87]">% d'acompte</span>
            </label>
            <button
              type="button"
              data-testid="quote-invoice-deposit"
              onClick={async () => {
                const pct = Number(depositPercent);
                if (!pct || pct <= 0 || pct > 100) return toast.error("Indiquez un pourcentage entre 1 et 100.");
                try {
                  const { data } = await studio.post(`/quotes/${id}/invoice`, { kind: "acompte", deposit_percent: pct });
                  toast.success(`Facture d'acompte ${data.reference} créée.`);
                  navigate(`/studio/factures/${data.id}`);
                } catch (e) { toast.error(apiError(e, "Facturation impossible.")); }
              }}
              className={btnGhost}
            >
              <Receipt className="w-3.5 h-3.5" /> Facture d'acompte
            </button>
            <button
              type="button"
              data-testid="quote-invoice-final"
              onClick={async () => {
                try {
                  const { data } = await studio.post(`/quotes/${id}/invoice`, { kind: "finale" });
                  toast.success(`Facture finale ${data.reference} créée.`);
                  navigate(`/studio/factures/${data.id}`);
                } catch (e) { toast.error(apiError(e, "Facturation impossible.")); }
              }}
              className={btnPrimary}
            >
              <Receipt className="w-4 h-4" /> Facture finale
            </button>
          </div>
        </div>
      )}

      {hasReverseCharge && (
        <p data-testid="quote-reverse-charge-mention" className="mt-4 rounded-2xl border border-[#E2E7F7] bg-white px-5 py-4 text-xs font-semibold text-[#0F2645] leading-relaxed">
          Autoliquidation : TVA à acquitter par le cocontractant (art. 20 de l'A.R. n° 1 du 29/12/1992). Cette mention
          est reprise automatiquement sur le devis PDF.
        </p>
      )}

      {isOwner && (
      <div data-testid="quote-profitability" className="dv-navy mt-4 overflow-hidden rounded-2xl border border-[#3A5CC8]/40 p-5 text-white shadow-[0_18px_40px_-24px_rgba(8,16,77,0.7)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-heading text-base font-semibold inline-flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#9FB4F2]" /> Rentabilité — visible par vous uniquement
          </p>
          <div className="flex items-center gap-2">
            <span data-testid="prof-state" className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${MARGIN_PILL[marginState][1]}`}>{MARGIN_PILL[marginState][0]}</span>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9FB4F2]">
              Objectif {targetMargin} % · ouvrier {eur(hourlyCost)}/h
            </span>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#9FB4F2]">Marge</p>
            <p data-testid="prof-margin" className="font-mono text-xl font-bold tabular-nums">{eur(margin)}</p>
            <p data-testid="prof-margin-percent" className="text-xs text-[#C8D2EC]">{marginPercent.toFixed(1)} % du HTVA</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#9FB4F2]">Temps de main-d'œuvre</p>
            <p data-testid="prof-hours" className="font-mono text-xl font-bold tabular-nums">{hours.toFixed(1)} h</p>
            <p className="text-xs text-[#C8D2EC]">soit {eur(labourCost)} de main-d'œuvre</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#9FB4F2]">Marge par heure</p>
            <p data-testid="prof-margin-hour" className="font-mono text-xl font-bold tabular-nums">{hours ? `${eur(marginPerHour)}/h` : "—"}</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#9FB4F2]">Coûts</p>
            <p className="font-mono text-sm font-semibold">Achats {eur(prof.materialCost)}</p>
            <p className="font-mono text-sm font-semibold">Total {eur(prof.materialCost + labourCost)}</p>
          </div>
        </div>
        {(prof.missingCost > 0 || prof.missingDuration > 0) && (
          <p data-testid="prof-missing" className="mt-3 inline-flex items-start gap-2 text-xs font-medium text-[#C8D2EC]">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-300" />
            Chiffres à prendre avec prudence : coût d'achat manquant sur {prof.missingCost} ligne(s), durée manquante sur {prof.missingDuration} ligne(s).
          </p>
        )}
        {margin < 0 && <p className="mt-3 text-xs font-bold text-rose-200">Attention : ce devis est à perte en l'état.</p>}
      </div>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <h2 className="font-heading text-base font-semibold text-[#0F2645]">Vérifier et envoyer</h2>
          <div data-testid="quote-action-group" className="mt-3 flex flex-col gap-2">
            <button type="button" data-testid="quote-send-btn" disabled={busy === "send"} onClick={sendToClient} className={`${btnPrimary} h-12 w-full text-base sm:h-11 sm:text-sm`}>
              {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer au client
            </button>
            {["sent", "viewed"].includes(quote.status) && (
              <button type="button" data-testid="quote-remind-btn" onClick={remind} className={`${btnGhost} h-12 w-full sm:h-11`}>
                <BellRing className="w-4 h-4 text-[#3A5CC8]" /> Relancer le client
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="quote-pdf-btn" disabled={busy === "pdf"} onClick={downloadPdf} className={`${btnGhost} h-12 sm:h-11`}>
                {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 text-[#3A5CC8]" />} PDF
              </button>
              <MoreActionsMenu
                testId="quote-more-actions"
                className="h-12 sm:h-11"
                items={[
                  { id: "review", label: "Relire avec l'IA", icon: Lightbulb, busy: busy === "review", onClick: runReview, testId: "quote-review-btn" },
                  { id: "duplicate", label: "Dupliquer ce devis", icon: CopyPlus, onClick: () => setDuplicating(true), testId: "quote-duplicate-btn" },
                  { id: "template", label: "Enregistrer comme modèle", icon: BookmarkPlus, onClick: () => setSavingTemplate(true), testId: "quote-template-btn" },
                  { id: "sep1", separator: true },
                  { id: "link", label: "Copier le lien client", icon: Copy, onClick: () => copyToClipboard(publicLink), testId: "quote-copy-link" },
                  !quote.signature && quote.status !== "refused" && { id: "onsite", label: "Faire signer sur place", icon: PenLine, onClick: () => setShowSign(true), testId: "quote-onsite-btn" },
                ]}
              />
            </div>
          </div>

          {quote.signature ? (
            <div data-testid="quote-signature" className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-bold text-green-800">
                Signé par {quote.signature.name} le {beDate(quote.signature.signed_at)}
              </p>
              <p data-testid="quote-sign-trace" className="mt-1 text-[11px] leading-relaxed text-green-900/70">
                {quote.signature.method === "sur-place" ? "Signé sur place, sur votre appareil" : "Signé à distance via le lien sécurisé"}
                {quote.signature.email ? ` · ${quote.signature.email}` : ""}
                {quote.signature.ip ? ` · IP ${quote.signature.ip}` : ""} · horodatage conservé comme preuve (eIDAS)
              </p>
            </div>
          ) : quote.status !== "refused" && showSign && (
            <div className="mt-4 border-t border-[#E2E7F7] pt-4">
                <div data-testid="quote-onsite-block">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#5C6A87]">Signature du client, ici et maintenant</p>
                  <p className="mt-1 text-xs text-[#5C6A87]">Tendez votre téléphone au client : il relit le total et signe au doigt.</p>
                  <SignaturePad ref={pad} testId="quote-signature" className="mt-3 max-w-lg" />
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input data-testid="quote-sign-name" className={`${inputCls} w-52`} placeholder="Nom du client *" value={signName} onChange={(e) => setSignName(e.target.value)} />
                    <input data-testid="quote-sign-email" type="email" className={`${inputCls} w-56`} placeholder="E-mail (facultatif)" value={signEmail} onChange={(e) => setSignEmail(e.target.value)} />
                    {optionNames.length > 0 && (
                      <select data-testid="quote-sign-option" className={`${inputCls} w-56`} value={signOption} onChange={(e) => setSignOption(e.target.value)}>
                        <option value="">Option retenue par le client *</option>
                        {optionTotals.map((o) => <option key={o.name} value={o.name}>{o.name} — {eur(o.net + o.vat)}</option>)}
                      </select>
                    )}
                    <button type="button" data-testid="quote-sign-confirm" disabled={busy === "onsite"} onClick={signOnsite} className={btnPrimary}>
                      {busy === "onsite" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Valider la signature
                    </button>
                    <button type="button" data-testid="quote-sign-cancel" onClick={() => setShowSign(false)} className={btnGhost}>Annuler</button>
                  </div>
                  <p className="mt-3 inline-flex items-start gap-2 text-[11px] leading-relaxed text-[#93A0BC]">
                    <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" />
                    Signature horodatée avec l'adresse IP de l'appareil, conservée comme preuve (eIDAS). Le client reçoit une copie par e-mail s'il est renseigné.
                  </p>
                </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <h2 className="font-heading text-base font-semibold text-[#0F2645]">Transmettre à ma comptabilité</h2>
          <p className="mt-1 text-xs text-[#5C6A87]">Devisto établit vos factures. Transmettez-les à votre comptable ou à votre ERP en un clic.</p>
          {quote.status === "accepted" && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {["billit", "yuki", "odoo"].map((provider) => (
                <button
                  key={provider}
                  type="button"
                  data-testid={`push-${provider}-btn`}
                  disabled={busy === provider}
                  onClick={() => pushTo(provider)}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ${quote.pushed_to === provider ? "bg-green-50 border border-green-200 text-green-700" : "bg-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.14)] text-white hover:bg-[#2F4CA8]"} disabled:opacity-40`}
                >
                  {busy === provider ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {quote.pushed_to === provider ? `${PROVIDERS[provider]} ✓` : PROVIDERS[provider]}
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" data-testid="export-copy-btn" onClick={() => exportQuote("copy")} className={btnGhost}>
              <Copy className="w-3.5 h-3.5" /> Copier le devis
            </button>
            <button type="button" data-testid="export-csv-btn" onClick={() => exportQuote("csv")} className={btnGhost}>
              <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {review && (
        <div data-testid="quote-review-result" className="mt-4 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E2E7F7] bg-[#F6F8FD]/70 px-5 py-4">
            <div className="min-w-0">
              <h2 className="font-heading text-base font-bold text-[#0F2645]">Relecture de l'assistant</h2>
              {review.verdict && <p className="mt-1 max-w-xl text-sm leading-relaxed text-[#5C6A87]">{review.verdict}</p>}
            </div>
            <div
              data-testid="review-score"
              className={`inline-flex shrink-0 flex-col items-center rounded-2xl px-4 py-2 ${
                review.score >= 85 ? "bg-emerald-50 text-emerald-700" : review.score >= 60 ? "bg-amber-50 text-amber-800" : "bg-rose-50 text-rose-700"
              }`}
            >
              <span className="font-heading text-xl font-extrabold tabular-nums">{review.score}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">complétude</span>
            </div>
          </div>

          <div className="space-y-4 px-5 py-4">
            {!review.warnings.length && !review.missing_lines.length && (
              <p data-testid="review-clean" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <ShieldCheck className="h-4 w-4" /> Aucun oubli détecté, le devis est prêt à partir.
              </p>
            )}

            {!!review.warnings.length && (
              <div data-testid="review-warnings" className="space-y-2">
                {review.warnings.map((w, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 ${
                      w.level === "bloquant" ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"
                    }`}
                  >
                    <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${w.level === "bloquant" ? "text-rose-600" : "text-amber-600"}`} />
                    <p className={`text-sm leading-relaxed ${w.level === "bloquant" ? "text-rose-900" : "text-amber-900"}`}>
                      {w.level === "bloquant" && <span className="mr-1.5 font-bold uppercase text-[10px] tracking-wider">Bloquant</span>}
                      {w.text}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {!!review.missing_lines.length && (
              <div data-testid="review-missing-lines">
                <p className="text-xs font-bold uppercase tracking-wider text-[#93A0BC]">Lignes probablement oubliées</p>
                <div className="mt-2 space-y-2">
                  {review.missing_lines.map((m) => (
                    <div key={m.id} data-testid={`review-missing-${m.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E7F7] px-3.5 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#0F2645]">{m.description}</p>
                        <p className="mt-0.5 text-xs text-[#5C6A87]">
                          {m.quantity} {m.unit} · {eur(m.unit_price)} HTVA · TVA {m.vat_percent} %
                          {m.service_name ? ` · depuis « ${m.service_name} »` : ""}
                        </p>
                        {m.reason && <p className="mt-1 text-xs italic leading-relaxed text-[#93A0BC]">{m.reason}</p>}
                      </div>
                      <button
                        type="button"
                        data-testid={`review-add-${m.id}`}
                        onClick={() => addMissingLine(m)}
                        className="inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-xl bg-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.14)] px-4 text-xs font-bold text-white transition-transform active:scale-95"
                      >
                        <Plus className="h-3.5 w-3.5" /> Ajouter
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {review.client_note && (
              <div data-testid="review-client-note" className="rounded-xl border border-[#C9D4F4] bg-[#EEF1FC]/60 px-3.5 py-3">
                <p className="text-xs font-bold uppercase tracking-wider text-[#2F4CA8]">Note rédigée pour le client</p>
                <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-[#0F2645]">{review.client_note}</p>
                <button
                  type="button"
                  data-testid="review-use-note"
                  onClick={useClientNote}
                  className="mt-2.5 inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border border-blue-300 bg-white px-3.5 text-xs font-bold text-[#2F4CA8] active:scale-95"
                >
                  <Plus className="h-3.5 w-3.5" /> Ajouter aux remarques
                </button>
              </div>
            )}

            {!!review.next_actions.length && (
              <div data-testid="review-next-actions">
                <p className="text-xs font-bold uppercase tracking-wider text-[#93A0BC]">Avant d'envoyer</p>
                <ul className="mt-2 space-y-1.5">
                  {review.next_actions.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#5C6A87]">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#3A5CC8]" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!!review.missing_lines.length && (
              <p className="text-xs text-[#93A0BC]">
                Les lignes ajoutées ne sont pas encore enregistrées : cliquez sur « Enregistrer » en haut du devis.
              </p>
            )}
          </div>
        </div>
      )}
      <MeasureCamera open={measuring} onClose={() => setMeasuring(false)} vatPercent={fiscalRules?.standard_rate ?? (vatRates.includes(21) ? 21 : vatRates[0])} onAdd={(newLines) => setLines((prev) => [...prev.filter((l) => (l.description || "").trim()), ...newLines])} />
      <PdfPreviewDialog open={preview} onClose={() => setPreview(false)} url={`/quotes/${id}/pdf`} filename={`devis-${quote.number}.pdf`} title={`Devis ${quote.number} — aperçu client`} editLabel={editable ? "Retour à l'édition" : "Retour au devis"} testId="quote-pdf-preview" />
      {duplicating && <DuplicateQuoteDialog quote={quote} onClose={() => setDuplicating(false)} />}
      {savingTemplate && <SaveTemplateDialog quote={{ ...quote, lines }} onClose={() => setSavingTemplate(false)} />}
    </StudioLayout>
  );
}
